import { useState } from 'react';
import { api } from '../lib/api';

/**
 * Premium (paid, on-demand) Esri "Spending & Segments" layer.
 *
 * Renders a single button below the free demographics. It calls Esri GeoEnrichment
 * ONLY when the user taps it (never auto-loads), so cost stays opt-in (~$0.08/tap).
 * Works for either ring buffers ({lat,lng,radii}) or drive-time polygons ({polygons}).
 */

interface SpendingCategory {
  key: string;
  label: string;
  avgPerHousehold: number | null;
  spi: number | null;
}
interface TapestrySegment {
  rank: number;
  name: string | null;
  code: string | null;
  pct: number | null;
}
interface SpendingArea {
  label: string;
  radiusMiles?: number;
  minutes?: number;
  population: number | null;
  households: number | null;
  medianHouseholdIncome: number | null;
  perCapitaIncome: number | null;
  daytimePopulation: number | null;
  daytimeWorkers: number | null;
  medianDisposableIncome: number | null;
  medianNetWorth: number | null;
  spending: SpendingCategory[];
  retailDemand: number | null;
  retailSupply: number | null;
  retailGap: number | null;
  retailLeakageFactor: number | null;
  tapestrySegments: TapestrySegment[];
  lifeMode: string | null;
}
interface SpendingResult {
  source: string;
  attributesBilled: number;
  areas: SpendingArea[];
}

interface Props {
  lat?: number;
  lng?: number;
  radii?: number[];
  polygons?: { minutes?: number; geometry: any }[];
  onLoaded?: (result: SpendingResult) => void;
}

const money = (n: number | null | undefined) =>
  n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });

const compactMoney = (n: number | null | undefined) => {
  if (n == null) return '—';
  const s = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1e9) return `${s}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${s}$${Math.round(a / 1e3)}K`;
  return `${s}$${Math.round(a)}`;
};

// SPI: 100 = U.S. average. Green above average, amber below, red well below.
function spiClasses(spi: number | null): string {
  if (spi == null) return 'bg-gray-100 text-gray-500';
  if (spi >= 100) return 'bg-success-wash text-success';
  if (spi >= 80) return 'bg-warning-wash text-warning';
  return 'bg-primary-100 text-primary-500';
}

export default function SpendingSegmentsPanel({ lat, lng, radii, polygons, onLoaded }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SpendingResult | null>(null);

  const canRun = polygons ? polygons.length > 0 : lat != null && lng != null;

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const res: SpendingResult = polygons
        ? await api.getSpendingForPolygons(polygons.map((p) => ({ minutes: p.minutes, geometry: p.geometry })))
        : await api.getSpending(lat as number, lng as number, radii && radii.length ? radii : [1, 3, 5]);
      setResult(res);
      onLoaded?.(res);
    } catch (e: any) {
      setError(e?.message || 'Failed to load Spending & Segments');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-3">
      {!result && (
        <button
          onClick={run}
          disabled={loading || !canRun}
          className="w-full flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-left transition-colors hover:border-gray-300 disabled:opacity-60"
        >
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">Spending &amp; Segments</span>
              <span className="rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-500">Premium · Esri</span>
            </div>
            <p className="mt-0.5 text-[11px] text-gray-500">
              Consumer spending + Tapestry lifestyle segments · on-demand (~$0.08/lookup)
            </p>
          </div>
          <span className="shrink-0 text-primary-500">
            {loading ? (
              <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            ) : (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
              </svg>
            )}
          </span>
        </button>
      )}

      {error && (
        <div className="mt-2 rounded-lg border border-primary-200 bg-primary-100 p-3 text-xs text-primary-500">
          {error}
          <button onClick={run} className="ml-2 font-semibold underline">Retry</button>
        </div>
      )}

      {result && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              Spending &amp; Segments
              <span className="rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-500">Premium · Esri</span>
            </span>
            <button onClick={() => setResult(null)} className="text-[11px] text-gray-400 hover:text-gray-600">Reset</button>
          </div>

          {result.areas.map((a) => (
            <div key={a.label} className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
              <p className="mb-2 text-sm font-semibold text-gray-900">{a.label}</p>

              {/* Consumer spending */}
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Avg annual spend / household</p>
              <div className="space-y-1">
                {a.spending.map((s) => (
                  <div key={s.key} className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">{s.label}</span>
                    <span className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{money(s.avgPerHousehold)}</span>
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${spiClasses(s.spi)}`} title="Spending Potential Index (100 = U.S. average)">
                        SPI {s.spi ?? '—'}
                      </span>
                    </span>
                  </div>
                ))}
              </div>

              {/* Income / wealth extras (Census can't provide these) */}
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 border-t border-gray-100 pt-2 text-[11px]">
                <div><span className="text-gray-400">Median disposable</span><br /><span className="font-semibold text-gray-900">{money(a.medianDisposableIncome)}</span></div>
                <div><span className="text-gray-400">Median net worth</span><br /><span className="font-semibold text-gray-900">{money(a.medianNetWorth)}</span></div>
                <div><span className="text-gray-400">Daytime pop</span><br /><span className="font-semibold text-gray-900">{a.daytimePopulation != null ? Number(a.daytimePopulation).toLocaleString() : '—'}</span></div>
                <div><span className="text-gray-400">Daytime workers</span><br /><span className="font-semibold text-gray-900">{a.daytimeWorkers != null ? Number(a.daytimeWorkers).toLocaleString() : '—'}</span></div>
              </div>

              {/* Retail opportunity (demand vs supply) */}
              {a.retailGap != null && (
                <div className="mt-2 border-t border-gray-100 pt-2 text-[11px]">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold uppercase tracking-wide text-gray-400">Retail {a.retailGap > 0 ? 'leakage' : 'surplus'}</span>
                    <span className={`font-semibold ${a.retailGap > 0 ? 'text-success' : 'text-gray-700'}`}>{compactMoney(Math.abs(a.retailGap))}/yr</span>
                  </div>
                  <p className="mt-0.5 text-gray-500">
                    Demand {compactMoney(a.retailDemand)} vs supply {compactMoney(a.retailSupply)}
                    {a.retailGap > 0 ? ' — unmet demand (new-tenant opportunity)' : ' — a retail destination'}
                  </p>
                </div>
              )}

              {/* Tapestry lifestyle segmentation */}
              {a.tapestrySegments.length > 0 && (
                <div className="mt-2 border-t border-gray-100 pt-2">
                  <p className="mb-1 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    <span>Top lifestyle segments</span>
                    {a.lifeMode && <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium normal-case text-gray-700">{a.lifeMode}</span>}
                  </p>
                  <div className="space-y-1.5">
                    {a.tapestrySegments.map((s) => (
                      <div key={s.rank}>
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium text-gray-800">{s.name}{s.code ? <span className="ml-1 text-gray-400">({s.code})</span> : null}</span>
                          <span className="font-semibold text-gray-900">{s.pct != null ? `${s.pct}%` : '—'}</span>
                        </div>
                        <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-gray-100">
                          <div className="h-full rounded-full bg-primary-500" style={{ width: `${Math.min(100, Number(s.pct) || 0)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          <p className="text-[11px] text-gray-400">
            Source: {result.source}. Billed {result.attributesBilled} attributes (~${(result.attributesBilled / 1000).toFixed(2)}).
            SPI 100 = U.S. average.
          </p>
        </div>
      )}
    </div>
  );
}
